import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKit } from "@cometchat/chat-uikit-react";
import { initials, avatarColorClass } from "./format";
import { CameraIcon } from "./Icons";
import {
  STATUS_PRESETS,
  StatusPreset,
  readStatus,
  readPhone,
  readDesignation,
  updateMyMetadata,
  parseMeta,
} from "./status";
import { fetchDirectoryScope, companyLabelFor } from "./directoryScope";
import { uploadAvatarPhoto } from "./avatarUpload";
import { setAvatarUrl as cacheAvatarUrl } from "./avatars";
import * as sounds from "./sounds";

// Matches DBMGate.tsx's LOCAL_SESSION_KEY — clearing it is what forces the
// next visit through a fresh OTP verification instead of the stored-session
// fast path.
const LOCAL_SESSION_KEY = "dbm-local-session-token";

/** Self view: your own profile, editable (status, phone + visibility, photo
 * upload placeholder). Other-user view: read-only info + a Message button
 * that jumps straight to the DM. */
export default function ProfilePanel({
  uid,
  meUid,
  onClose,
  onMessage,
}: {
  uid: string;
  meUid?: string;
  onClose: () => void;
  onMessage?: (uid: string, name: string) => void;
}) {
  const isSelf = !!meUid && uid === meUid;
  const navigate = useNavigate();
  const [user, setUser] = useState<CometChat.User | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const [preset, setPreset] = useState<StatusPreset>("online");
  const [reason, setReason] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneVisible, setPhoneVisible] = useState(false);
  const [designation, setDesignation] = useState("");

  // Email and company from the directory scope. CometChat's dbmEmail
  // metadata is only written once that person has signed in, so relying
  // on it alone left this panel showing "Not set" for almost everyone —
  // Supabase Auth (which the scope reads) has it for all of them.
  const [org, setOrg] = useState<{ email: string; company: string | null } | null>(null);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = isSelf ? CometChat.getLoggedinUser() : CometChat.getUser(uid);
    fetch
      .then((u) => {
        if (cancelled || !u) return;
        setUser(u as CometChat.User);
        setAvatarUrl((u as CometChat.User).getAvatar?.() || null);
        const st = readStatus(u as CometChat.User);
        if (st) {
          setPreset(st.preset);
          setReason(st.reason || "");
        }
        const ph = readPhone(u as CometChat.User);
        setPhone(ph.phone || "");
        setPhoneVisible(ph.visible);
        setDesignation(readDesignation(u as CometChat.User) || "");
      })
      .catch((e) => console.warn("profile load failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    fetchDirectoryScope()
      .then((s) => {
        if (cancelled) return;
        const entry = s.people[uid];
        setOrg({ email: entry?.email || "", company: companyLabelFor(entry) });
      })
      .catch(() => {
        // Falls back to the metadata copy below.
      });
    return () => {
      cancelled = true;
    };
  }, [uid, isSelf]);

  const otherStatus = !isSelf ? readStatus(user) : null;
  const otherPhone = !isSelf ? readPhone(user) : null;
  const otherDesignation = !isSelf ? readDesignation(user) : undefined;
  // Scope first (Supabase Auth, set for everyone), then the metadata copy
  // that login writes (see _lib/cometchat.js's setUserEmail) as a fallback
  // for anyone the scope doesn't cover.
  const email: string | undefined = org?.email || (parseMeta(user).dbmEmail as string | undefined);

  const saveStatus = async () => {
    sounds.playOnce(sounds.SOUND.click);
    setSaving(true);
    try {
      await updateMyMetadata({ dbmStatus: { preset, reason: reason.trim() || undefined } });
    } catch (e) {
      console.warn("status save failed", e);
    } finally {
      setSaving(false);
    }
  };

  const saveDesignation = async () => {
    sounds.playOnce(sounds.SOUND.click);
    setSaving(true);
    try {
      await updateMyMetadata({ dbmDesignation: designation.trim() || undefined });
    } catch (e) {
      console.warn("designation save failed", e);
    } finally {
      setSaving(false);
    }
  };

  const savePhone = async () => {
    sounds.playOnce(sounds.SOUND.click);
    setSaving(true);
    try {
      await updateMyMetadata({ dbmPhone: phone.trim() || undefined });
    } catch (e) {
      console.warn("phone save failed", e);
    } finally {
      setSaving(false);
    }
  };

  const onPickPhoto = async (file: File) => {
    sounds.playOnce(sounds.SOUND.click);
    setAvatarBusy(true);
    setAvatarError("");
    try {
      const url = await uploadAvatarPhoto(file);
      // The upload itself saves the avatar via a server-side REST call
      // (the only way to set it after hosting the file), which the
      // client SDK has no way of knowing about — its own cached copy of
      // "who am I" would otherwise keep reporting no avatar until a
      // fresh login, undoing the change the moment this panel reopens.
      // Pushing it through updateCurrentUserDetails, the same client-side
      // path status/phone already use, keeps that cache in sync too.
      const current = await CometChat.getLoggedinUser();
      if (current) {
        current.setAvatar(url);
        await CometChat.updateCurrentUserDetails(current);
      }
      setAvatarUrl(url);
      // Push it into the shared cache so every other avatar in the app
      // (sidebar, message rows, facepile…) swaps to the new photo now
      // rather than only after a reload.
      cacheAvatarUrl(uid, url);
    } catch (e) {
      setAvatarError(e instanceof Error ? e.message : "Couldn't upload the photo — try again");
    } finally {
      setAvatarBusy(false);
    }
  };

  const togglePhoneVisible = async () => {
    const next = !phoneVisible;
    setPhoneVisible(next);
    try {
      await updateMyMetadata({ dbmPhoneVisible: next });
    } catch (e) {
      console.warn("phone visibility save failed", e);
      setPhoneVisible(!next);
    }
  };

  /** Clears the stored session token and logs out of CometChat, so the
   * next load runs a fresh OTP verification instead of the stored-session
   * fast path — the only way to force a stuck display name (or any other
   * server-side detail) to re-sync sooner than the token's own expiry. */
  const signOut = async () => {
    sounds.playOnce(sounds.SOUND.click);
    setSigningOut(true);
    try {
      await CometChatUIKit.logout();
    } catch (e) {
      console.warn("logout failed", e);
    }
    localStorage.removeItem(LOCAL_SESSION_KEY);
    navigate("/login", { replace: true });
  };

  const copyEmail = () => {
    if (!email) return;
    navigator.clipboard
      ?.writeText(email)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="profile-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <b>{isSelf ? "Your profile" : "Profile"}</b>
          <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {loading ? (
          <div className="modal-section">
            <div className="sect-empty">Loading…</div>
          </div>
        ) : (
          <>
            <div className="profile-hero">
              <div className="profile-hero__avatarwrap">
                {avatarUrl ? (
                  <img className="av profile-hero__avatar profile-hero__avatar--photo" src={avatarUrl} alt="" />
                ) : (
                  <div
                    className={`av profile-hero__avatar ${isSelf ? "" : avatarColorClass(uid)}`}
                    style={isSelf ? { background: "var(--orange)", color: "var(--ink)" } : undefined}
                  >
                    {initials(user?.getName())}
                  </div>
                )}
                {isSelf && (
                  <>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = ""; // allow re-picking the same file next time
                        if (file) void onPickPhoto(file);
                      }}
                    />
                    <button
                      className="profile-hero__camera"
                      title="Change photo"
                      aria-label="Change photo"
                      disabled={avatarBusy}
                      onClick={() => avatarInputRef.current?.click()}
                    >
                      {avatarBusy ? <span className="spin" /> : <CameraIcon size={14} />}
                    </button>
                  </>
                )}
              </div>
              <div className="profile-hero__name">{user?.getName() || uid}</div>
              {/* Self sees their title in the editable section below —
                  repeating it here would just be the same string twice. */}
              {!isSelf && otherDesignation && (
                <div className="profile-hero__role">{otherDesignation}</div>
              )}
              {org?.company && <div className="profile-hero__co">{org.company}</div>}
              {isSelf && avatarError && <div className="profile-hero__error">{avatarError}</div>}
              {!isSelf && otherStatus && (
                <div className="profile-hero__status">
                  <span className="status-dot" style={{ background: STATUS_PRESETS[otherStatus.preset].color }} />
                  {STATUS_PRESETS[otherStatus.preset].label}
                  {otherStatus.reason && <span> · {otherStatus.reason}</span>}
                </div>
              )}
            </div>

            {isSelf && (
              <div className="modal-section">
                <div className="modal-label">Status</div>
                <div className="status-picker">
                  {(Object.keys(STATUS_PRESETS) as StatusPreset[]).map((p) => (
                    <button
                      key={p}
                      className={`status-picker__opt ${preset === p ? "on" : ""}`}
                      onClick={() => setPreset(p)}
                    >
                      <span className="status-dot" style={{ background: STATUS_PRESETS[p].color }} />
                      {STATUS_PRESETS[p].label}
                    </button>
                  ))}
                </div>
                <input
                  className="profile-input"
                  type="text"
                  maxLength={80}
                  placeholder={preset === "ooo" ? "Reason — e.g. back Monday" : "Add a note (optional)"}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <button
                  className="modal-btn"
                  style={{ marginTop: 10 }}
                  disabled={saving}
                  onClick={() => void saveStatus()}
                >
                  {saving ? "Saving…" : "Save status"}
                </button>
              </div>
            )}

            {isSelf && (
              <div className="modal-section">
                <div className="modal-label">Designation</div>
                <div className="modal-row">
                  <input
                    className="profile-input"
                    type="text"
                    maxLength={60}
                    placeholder="e.g. Sales Manager"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && void saveDesignation()}
                  />
                  <button className="modal-btn" disabled={saving} onClick={() => void saveDesignation()}>
                    Save
                  </button>
                </div>
              </div>
            )}

            <div className="modal-section">
              <div className="modal-label">Email</div>
              <div className="profile-copy-row">
                <span className="profile-copy-row__value">{email || "Not set"}</span>
                {!!email && (
                  <button className="profile-copy-btn" onClick={copyEmail}>
                    {copied ? "Copied" : "Copy"}
                  </button>
                )}
              </div>
            </div>

            {isSelf ? (
              <div className="modal-section">
                <div className="modal-label">Phone</div>
                <div className="modal-row">
                  <input
                    className="profile-input"
                    type="tel"
                    placeholder="Add a phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <button className="modal-btn" disabled={saving} onClick={() => void savePhone()}>
                    Save
                  </button>
                </div>
                <label className="profile-toggle-row">
                  <input type="checkbox" checked={phoneVisible} onChange={() => void togglePhoneVisible()} />
                  Visible to others
                </label>
              </div>
            ) : (
              otherPhone?.visible &&
              otherPhone.phone && (
                <div className="modal-section">
                  <div className="modal-label">Phone</div>
                  <div className="profile-copy-row__value">{otherPhone.phone}</div>
                </div>
              )
            )}

            {isSelf && (
              <div className="modal-section details-actions">
                <button
                  className="details-action details-action--danger"
                  disabled={signingOut}
                  onClick={() => void signOut()}
                >
                  {signingOut ? "Signing out…" : "Sign out"}
                </button>
              </div>
            )}

            {!isSelf && onMessage && (
              <div className="modal-section details-actions">
                <button className="modal-btn" onClick={() => onMessage(uid, user?.getName() || uid)}>
                  Message
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
